import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER, ErrorHandler, Injector, NO_ERRORS_SCHEMA } from '@angular/core';
import { DateAdapter, MatNativeDateModule, MAT_DATE_LOCALE } from '@angular/material/core';
import { DateFnsAdapter, MatDateFnsModule } from '@angular/material-date-fns-adapter';
import { MatProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { NgbModule, NgbModalModule, NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { enAU } from 'date-fns/locale';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { LoginComponent } from './features/auth/login/login.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { VehiclesModule } from './features/vehicles/vehicles.module';
import { FilterModule } from './features/filter/filter.module';
import { CoreUiModule, ConfigService, IS_PROD, AuthTokenInterceptor, DataPersistence } from '@pos-app/core-ui';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { GlobalErrorHandler } from './core/services/global.errorHandler';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatChipsModule as MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule as MatInputModule } from '@angular/material/input';
import { MatTooltipModule as MatTooltipModule } from '@angular/material/tooltip';
import { MatRadioModule as MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule as MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule as MatTableModule } from '@angular/material/table';
import { MatSelectModule as MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule as MatAutocompleteModule } from '@angular/material/autocomplete';
import { ForgotPasswordComponent } from './features/auth/forgotpassword/forgotpassword.component';
import { SignUpComponent } from './features/auth/signup/signup.component';
import { OrdersModule } from './features/orders/orders.module';
import { ResetPasswordComponent } from './features/auth/resetpassword/resetpassword.component';
import { CustomersModule } from './features/customers/customers.module';
import { NgSelectModule } from '@ng-select/ng-select';
import { ManageusersModule } from './features/manageusers/manageusers.module';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { MatStepperModule } from '@angular/material/stepper';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS as MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule as MatFormFieldModule } from '@angular/material/form-field';
import { LoginToComponent } from './features/auth/logintoshow/login-to.component';
import { ContentComponent } from './features/content/content.component';
import { CamperTrailerComponent } from './features/camper-trailer/containers/camper-trailer/camper-trailer.component';
import { CamperTrailerDetailsComponent } from './features/camper-trailer/components/camper-trailer-details/camper-trailer-details.component';
import { CamperServiceDetailsComponent } from './features/camper-trailer/components/camper-service-details/camper-service-details.component';
import { CamperCustomerDetailsComponent } from './features/camper-trailer/components/camper-customer-details/camper-customer-details.component';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { ResourcesModule } from './features/resources/resources.module';
import { RegisterAdminModule } from './features/register-admin/register-admin.module';
import { MatButtonModule as MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatDialogModule as MatDialogModule } from '@angular/material/dialog';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CoreModule } from './core/core.module';
import { EpcModule } from './features/epc/epc.module';
import { AppConsts } from './features/epc/constants/AppConsts';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { LegendComponent } from './features/scheduler/legend/legend.component';
import { SchedulerComponent } from './features/scheduler/scheduler.component';
import { SchedulerWeekComponent } from './features/scheduler/schedulerWeek/schedulerWeek.component';
import { SchedulerSetHours } from './features/scheduler/schedulerHours/schedulerSetHours.component';
import { SchedulerSetWorkshopHours } from './features/scheduler/workshop/schedulerHours/schedulerSetWorkshopHours.component';
import { SchedulerSetWorkshopDays } from './features/scheduler/workshop/schedulerDays/schedulerSetWorkshopDays.component';
import { NgbdModalResourceContent } from './features/scheduler/resource/resource.component';
import { NgbdModalOrderDetailCard } from './features/scheduler/orderDetailCard/orderDetailCard.component';
import { NgbdModalTaskDetailCard } from './features/scheduler/taskDetailCard/taskDetailCard.component';
import { SplitOrderContent } from './features/scheduler/splitorder/splitorder.component';
import { OrderCardNotesComponent } from './features/scheduler/orderDetailCard/orderCardNotes/order-card-notes.component';
import { SalesOrderlineNotesComponent } from './features/scheduler/orderDetailCard/salesOrderLineNotes/sales-orderline-notes.component';
import { ScheduleHistoryNotesComponent } from './features/scheduler/orderDetailCard/scheduleHistoryNotes/schedule-history-notes.component';
import { MatPaginatorModule as MatPaginatorModule } from '@angular/material/paginator';
import { ManagepartnerModule } from './features/partner/managepartner.module';
import { MatGridListModule } from '@angular/material/grid-list';
import { CentralModule } from './features/central/central.module';
import { WarrantyformModule } from './features/warrantyform/warrantyform.module';

const appInitializerFn = (injector: Injector) => {
  return () => {
    const configService = injector.get(ConfigService);

    return configService.loadAppConfig().then((value) => {
      const appConfig = configService.AppConfig;
      AppConsts.ooeAPIBaseUrl = appConfig.backendUrl;
      AppConsts.brand = appConfig.brand;
    });
  };
};

@NgModule({ declarations: [
        AppComponent,
        LoginComponent,
        SignUpComponent,
        ForgotPasswordComponent,
        ResetPasswordComponent,
        LoginToComponent,
        ContentComponent,
        CamperTrailerComponent,
        CamperTrailerDetailsComponent,
        CamperServiceDetailsComponent,
        CamperCustomerDetailsComponent,
        LegendComponent,
        SchedulerComponent,
        SchedulerWeekComponent,
        SchedulerSetHours,
        SchedulerSetWorkshopHours,
        SchedulerSetWorkshopDays,
        NgbdModalResourceContent,
        NgbdModalOrderDetailCard,
        NgbdModalTaskDetailCard,
        SplitOrderContent,
        OrderCardNotesComponent,
        SalesOrderlineNotesComponent,
        ScheduleHistoryNotesComponent,
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        CoreModule,
        NgbModule,
        NgbAccordionModule,
        AccordionModule,
        FormsModule,
        ReactiveFormsModule,
        CoreUiModule,
        BrowserAnimationsModule,
        MatChipsModule,
        MatIconModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatInputModule,
        MatFormFieldModule,
        MatAutocompleteModule,
        MatStepperModule,
        MatRadioModule,
        MatSlideToggleModule,
        MatTableModule,
        MatSelectModule,
        NgMultiSelectDropDownModule,
        MatPaginatorModule,
        ConfirmDialogModule,
        NgbModalModule,
        MatButtonModule,
        NgxSpinnerModule,
        ToastrModule.forRoot({
            maxOpened: 1,
            preventDuplicates: true,
            autoDismiss: true,
            onActivateTick: true,
        }),
        EffectsModule.forRoot([]),
        StoreModule.forRoot({}, {
            metaReducers: !environment.production ? [] : [],
            runtimeChecks: {
                strictActionImmutability: true,
                strictStateImmutability: true,
            },
        }),
        !environment.production ? StoreDevtoolsModule.instrument({ connectInZone: true }) : [],
        // modules
        VehiclesModule,
        FilterModule,
        RegisterAdminModule,
        ResourcesModule,
        CustomersModule,
        OrdersModule,
        ManageusersModule,
        NgSelectModule,
        AngularEditorModule,
        DragDropModule,
        MatDialogModule,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatDateFnsModule,
        MatSidenavModule,
        EpcModule,
        ManagepartnerModule,
        MatGridListModule,
        CentralModule,
        WarrantyformModule], providers: [
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthTokenInterceptor,
            multi: true,
        },
        ConfigService,
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializerFn,
            multi: true,
            deps: [Injector],
        },
        {
            provide: IS_PROD,
            useValue: environment.production,
        },
        DataPersistence,
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: { appearance: 'fill' },
        },
        ToastrService,
        {
            provide: MAT_DATE_LOCALE,
            useValue: enAU,
        },
        {
            provide: DateAdapter,
            useClass: DateFnsAdapter,
            //deps: [MAT_DATE_LOCALE],
        },
        provideHttpClient(withInterceptorsFromDi()),
        // PrimeNG v18+ requires an explicit theme preset; without this, p-table/p-panel/p-tree/
        // p-confirmDialog/p-paginator/p-inputNumber render unstyled after the v21 upgrade.
        providePrimeNG({
            theme: {
                preset: Aura,
                options: {
                    darkModeSelector: false,
                },
            },
        }),
    ] })
export class AppModule {}


